<template>
  <template v-if="column.recruiterOwnershipDays && column.recruiterOwnershipDaysLeft">
    <GhostComponent
      :title="$t('Staffing agency')"
      component-name-when-active="PopperCardButton"
      slot-to-render-when-inactive="btn"
      placement="top"
      strategy="absolute"
      :global="tableGlobals"
    >
      <template #btn>
        <WdsLabel
          sm
          subtle
          :error="column.recruiterOwnershipDaysLeft <= 0"
          :success="column.recruiterOwnershipDaysLeft > 0"
          class="m-r-5"
        >
          {{ column.recruiterOwnershipDaysLeft <= 0 ? $t('Expired') : $t('Active') }}
        </WdsLabel>
      </template>

      <template #popper>
        <div class="m-b-10">
          <WdsText>
            {{
              column.recruiterManagesWorkers ? $t('Managed by') : $t('Ongoing ownership period for')
            }}
            <strong>{{ column?.recruiter?.name }}</strong>
          </WdsText>
        </div>

        <div v-if="!column.recruiterManagesWorkers && additiveFeesText" class="row">
          <div class="col-sm-5 nowrap">
            <strong>{{ $t('Fee') }}:</strong>
          </div>
          <div class="col-sm-7 nowrap">{{ additiveFeesText }}</div>
        </div>
        <div class="row">
          <div class="col-sm-5 nowrap">
            <strong>{{ $t('Ownership Days') }}:</strong>
          </div>
          <div class="col-sm-7 nowrap">
            {{ column.recruiterOwnershipDays }}
            {{ column.recruiterOwnershipDays > 1 ? $t('days') : $t('day') }}
          </div>
        </div>
        <div class="row">
          <div class="col-sm-5 nowrap">
            <strong>{{ $t('Started') }}:</strong>
          </div>
          <div class="col-sm-7 nowrap">
            {{
              column.recruiterOwnershipStartDate
                ? moment(column.recruiterOwnershipStartDate).format('MMM DD, Y')
                : '-'
            }}
          </div>
        </div>
        <div class="row">
          <div class="col-sm-5 nowrap">
            <strong>{{ $t(column.recruiterOwnershipDaysLeft <= 0 ? 'Ended' : 'Ends') }}:</strong>
          </div>
          <div class="col-sm-7 nowrap">
            {{ ownershipEndsAt }}
          </div>
        </div>
      </template>
    </GhostComponent>
  </template>
  <span v-else>-</span>
</template>

<script lang="ts" setup>
import moment from 'moment'
import { computed } from 'vue'
import { WdsLabel, WdsText } from '@worksome/wds-components'
import { FragmentType, graphql, useFragment } from '@/gql'
import { FeeApplication } from '@/gql/graphql'
import { $t } from '@/composables/translation'
import GhostComponent from '@/components/GhostComponent.vue'
import { useTableGlobalsSelector } from '@/components/GraphqlDataTable/injection'

const tableGlobals = useTableGlobalsSelector()

const HireHasStaffingAgencyColumn = graphql(`
  fragment HireHasStaffingAgencyColumn on Hire {
    recruiter {
      name
    }
    fees {
      id
      application
      formattedRate
    }
    recruiterOwnershipStartDate
    recruiterOwnershipDays
    recruiterOwnershipIsExpired
    recruiterOwnershipDaysLeft
    recruiterManagesWorkers
  }
`)

const props = defineProps<{ row: FragmentType<typeof HireHasStaffingAgencyColumn> }>()
const column = computed(() => useFragment(HireHasStaffingAgencyColumn, props.row))
const additiveFeesText = computed(() =>
  column.value.fees
    .filter((fee) => fee.application === FeeApplication.Additive)
    .map((fee) => fee.formattedRate)
    .join(' + '),
)

const ownershipEndsAt = computed<string>(() => {
  if (!column.value.recruiterOwnershipStartDate || !column.value.recruiterOwnershipDays) {
    return '-'
  }

  return moment(column.value.recruiterOwnershipStartDate)
    .add(Math.max(column.value.recruiterOwnershipDays - 1, 0), 'days')
    .format('MMM DD, Y')
})
</script>

<style lang="scss">
.hire-column-worker-information {
  text-align: left;
}
</style>
